import React, { FC, JSX, memo, useEffect, useState } from 'react';
import { SkeletonBlock } from '@one-thd/sui-atomic-components';
import { UNAVAILABLE_IMAGE_URL } from '../../constants/general';

interface IProgressiveImageProps {
  src: string;
  alt: string;
  lazy?: boolean;
  width?: string | number;
  height?: string | number;
  className?: string;
  fetchPriority?: 'high' | 'low' | 'auto';
  fallbackSrc?: string; // Optional prop to specify a custom fallback image URL in case of load failure
}

/**
 * A React component that progressively loads an image with a fallback option.
 * @params {IProgressiveImageProps} props - The props for the ProgressiveImage component.
 * @returns {JSX.Element} The rendered image or a skeleton placeholder while loading.
 */
export const ProgressiveImage: FC<IProgressiveImageProps> = memo(
  ({
    src,
    alt,
    lazy = true,
    width,
    height,
    className,
    fetchPriority,
    fallbackSrc = UNAVAILABLE_IMAGE_URL,
  }): JSX.Element => {
    const [resolvedSrc, setResolvedSrc] = useState<string | null>(null);

    useEffect(() => {
      setResolvedSrc(null);
      const img = new Image();
      img.src = src;
      img.onload = () => setResolvedSrc(src);
      img.onerror = () => setResolvedSrc(fallbackSrc);
      return () => {
        img.onload = null;
        img.onerror = null;
      };
    }, [src, fallbackSrc]);

    if (resolvedSrc === null) {
      return (
        <div style={{ width, height }}>
          <SkeletonBlock width={width} height={height} />
        </div>
      );
    }

    return (
      <img
        loading={lazy ? 'lazy' : undefined}
        src={resolvedSrc}
        alt={alt}
        width={width}
        height={height}
        className={className}
        fetchPriority={fetchPriority}
      />
    );
  },
);
